import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { SALESFORCE_OBJECT_TYPES } from "@/model/interface/Integration";
import {
  useGetSalesforceListViewsQuery,
  useImportSalesforceListViewMutation,
  useGetCampaignsQuery,
  useGetContactListsQuery,
} from "@/store/rest_api";
import { CampaignStatus } from "@/model/interface/Campaign";
import toast from "react-hot-toast";

export default function SalesforceImport() {
  const [objectType, setObjectType] = useState("LEAD");
  const [listViewId, setListViewId] = useState("");
  const [targetType, setTargetType] = useState<"campaign" | "contactList" | "none">("none");
  const [targetId, setTargetId] = useState("");

  const { data: listViews, isLoading: listViewsLoading } = useGetSalesforceListViewsQuery(objectType);
  const { data: campaigns } = useGetCampaignsQuery();
  const { data: contactLists } = useGetContactListsQuery({});
  const [importListView, { isLoading: isImporting }] = useImportSalesforceListViewMutation();

  const handleImport = async () => {
    if (!listViewId) {
      toast.error("Select a list view");
      return;
    }

    try {
      const result = await importListView({
        objectType,
        listViewId,
        campaignId: targetType === "campaign" ? targetId : undefined,
        contactListId: targetType === "contactList" ? targetId : undefined,
      }).unwrap();

      toast.success(
        `Imported ${result.imported} contacts (${result.newContacts} new) from ${result.total} records`
      );
    } catch {
      toast.error("Import failed");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Import from Salesforce List View</CardTitle>
        <CardDescription>
          Pull contacts from a Salesforce list view into a campaign or contact list
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Object Type</Label>
            <Select value={objectType} onValueChange={(value) => { setObjectType(value); setListViewId(""); }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SALESFORCE_OBJECT_TYPES.map((type) => (
                  <SelectItem key={type} value={type} className="capitalize">
                    {type.toLowerCase()}s
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>List View</Label>
            <Select value={listViewId} onValueChange={setListViewId} disabled={listViewsLoading}>
              <SelectTrigger>
                <SelectValue placeholder={listViewsLoading ? "Loading..." : "Select a list view"} />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {listViews?.map((view) => (
                  <SelectItem key={view.id} value={view.id}>
                    {view.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Import Into</Label>
            <Select value={targetType} onValueChange={(value) => { setTargetType(value as typeof targetType); setTargetId(""); }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Contacts only</SelectItem>
                <SelectItem value="campaign">Power Dialer Campaign</SelectItem>
                <SelectItem value="contactList">Contact List</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {targetType !== "none" && (
            <div className="space-y-2">
              <Label>{targetType === "campaign" ? "Campaign" : "Contact List"}</Label>
              <Select value={targetId} onValueChange={setTargetId}>
                <SelectTrigger>
                  <SelectValue placeholder={`Select ${targetType === "campaign" ? "campaign" : "list"}`} />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {targetType === "campaign" && campaigns?.filter((campaign) => campaign.status !== CampaignStatus.ARCHIVED).map((campaign) => (
                    <SelectItem key={campaign.id} value={campaign.id!}>
                      {campaign.name}
                    </SelectItem>
                  ))}
                  {targetType === "contactList" && contactLists?.items?.map((list) => (
                    <SelectItem key={list.id} value={list.id}>
                      {list.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <Button onClick={handleImport} disabled={isImporting || !listViewId}>
          {isImporting ? "Importing..." : "Import"}
        </Button>
      </CardContent>
    </Card>
  );
}
